import { useMemo, useState } from 'react';
import { joursFeries } from '../../../server/lib/dates';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { congesApi, type DemandeAbsence } from '../../services/conges-api';
import { NavigationMois, moisDecale } from '../../components/ndf/ui/NavigationMois';
import { moisCourant, useMoisDeSession } from '../../hooks/useMoisDeSession';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BoutonSecondaire, Champ, Section, Selection } from '../../components/ndf/ui/Champ';
import { FormulaireDemande } from '../../components/conges/FormulaireDemande';
import { COULEUR_STATUT, LIBELLE_STATUT } from './CongesDemandesPage';
import { periodeFr } from '../../lib/periode-conges';

/**
 * Mon calendrier (R-CAL-01).
 *
 * Deux usages en un écran : VOIR ce qui est posé, et POSER. Le second est celui
 * qui compte — c'est là que se joue l'objectif de vingt secondes du skill
 * ndf-ux. Un tap sur un jour propose journée / matin / après-midi ; un second
 * tap sur un jour plus loin sélectionne la plage. Le formulaire s'ouvre ensuite
 * prérempli, et le salarié n'a plus qu'à choisir le type.
 *
 * Les week-ends et les jours fériés sont grisés et NON sélectionnables : poser
 * un congé un dimanche ne veut rien dire, et laisser le faire pour l'annoncer
 * ensuite ferait perdre le temps qu'on essaie de gagner.
 */

const JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

/**
 * Les fériés d'une année, Pentecôte comprise.
 *
 * Ils viennent de `server/lib/dates.ts`, la SEULE arithmétique de dates du
 * dépôt — module PUR, donc importable ici comme `personnes.ts` l'est déjà.
 *
 * L'écran les recalculait : algorithme de Pâques réécrit et huit fériés fixes
 * en dur. Deux implémentations de la même règle divergent sans prévenir.
 *
 * Le lundi de Pentecôte est PARAMÉTRABLE, et le paramètre est lu ici comme le
 * serveur le lit (`services/conges/calendrier.ts`) : sans lui, un jour pouvait
 * être grisé à l'écran et décompté par le serveur, ou l'inverse.
 *
 * Sert à GRISER, jamais à décompter : le décompte reste au serveur (R-FER-03).
 */
function feries(annee: number, pentecoteChomee: boolean): Set<string> {
  return new Set(joursFeries(annee, { lundiPentecoteChome: pentecoteChomee }).keys());
}

const iso = (a: number, m: number, j: number) =>
  `${a}-${String(m + 1).padStart(2, '0')}-${String(j).padStart(2, '0')}`;

/** La couleur d'un jour, selon le statut de l'absence qui l'occupe. */
const TEINTE_STATUT: Record<string, string> = {
  attente_manager: 'bg-jaune-fond/50 text-jaune',
  attente_rh: 'bg-jaune-fond/50 text-jaune',
  approuvee: 'bg-vert-fond/60 text-vert',
  refusee: 'bg-slate-100 text-slate-400 line-through',
  annulee: 'bg-slate-100 text-slate-400 line-through',
};

export function CongesCalendrierPage() {
  const navigate = useNavigate();
  // Le mois de travail survit à un aller-retour vers un autre écran
  // (Lot 7, point 10). Les deux nombres en sont dérivés : le reste de l'écran
  // raisonne en année et en index de mois.
  const [moisIso, setMoisIso] = useMoisDeSession('calendrier', moisCourant());
  const annee = Number(moisIso.slice(0, 4));
  const mois = Number(moisIso.slice(5, 7)) - 1;
  const [selection, setSelection] = useState<{ debut: string; fin: string } | null>(null);
  // Ce qu'on est en train de faire sous le calendrier : poser une absence, ou
  // regarder celle qui occupe un jour. Jamais les deux.
  const [consultee, setConsultee] = useState<DemandeAbsence | null>(null);
  const [filtreType, setFiltreType] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'demandes'],
    queryFn: () => congesApi.demandes.liste(),
  });
  // Le lundi de Pentecôte est paramétrable : on lit le MÊME paramètre que le
  // serveur. Un échec de lecture retombe sur « chômé », valeur par défaut du
  // paramètre — le grisage n'est qu'une aide à la saisie, il ne décompte rien.
  const { data: parametres } = useQuery({
    queryKey: ['conges', 'parametres'],
    queryFn: () => congesApi.parametres.liste(),
    retry: false,
  });
  const pentecoteChomee =
    (parametres?.parametres.find((p) => p.key === 'conges_lundi_pentecote_chome')?.value ?? 'true')
      .trim()
      .toLowerCase() !== 'false';

  const demandes = useMemo(() => {
    let liste = data?.demandes ?? [];
    if (filtreType) liste = liste.filter((d) => d.type_libelle === filtreType);
    if (filtreStatut) liste = liste.filter((d) => d.statut === filtreStatut);
    return liste;
  }, [data, filtreType, filtreStatut]);

  const typesPresents = useMemo(
    () => [...new Set((data?.demandes ?? []).map((d) => d.type_libelle ?? 'Absence'))].sort(),
    [data],
  );

  const parJour = useMemo(() => {
    const carte = new Map<string, DemandeAbsence>();
    for (const d of demandes) {
      // Les demandes closes n'occupent pas le calendrier, mais restent
      // affichées en estompé : elles expliquent pourquoi un jour est libre.
      for (let j = new Date(`${d.date_debut}T00:00:00Z`); j <= new Date(`${d.date_fin}T00:00:00Z`); j.setUTCDate(j.getUTCDate() + 1)) {
        const cle = j.toISOString().slice(0, 10);
        const existante = carte.get(cle);
        // Une demande vivante prime sur une demande close le même jour.
        if (!existante || ['refusee', 'annulee'].includes(existante.statut)) carte.set(cle, d);
      }
    }
    return carte;
  }, [demandes]);

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  const feriesAnnee = feries(annee, pentecoteChomee);
  const premier = new Date(Date.UTC(annee, mois, 1));
  // Lundi = 0 : la semaine française commence le lundi, pas le dimanche.
  const decalage = (premier.getUTCDay() + 6) % 7;
  const nbJours = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();

  const posable = (cle: string) => {
    const d = new Date(`${cle}T00:00:00Z`).getUTCDay();
    return d !== 0 && d !== 6 && !feriesAnnee.has(cle);
  };

  /** Le jour est-il déjà pris EN ENTIER par une absence vivante ? */
  const occupeEntierement = (cle: string): DemandeAbsence | null => {
    const a = parJour.get(cle);
    if (!a || ['refusee', 'annulee'].includes(a.statut)) return null;
    // Une demi-journée laisse l'autre moitié libre : c'est encore un jour où
    // l'on peut poser, et le clic doit continuer d'ouvrir la pose.
    // Une seule moitié laisse l'autre libre ; « matin → après-midi » prend la
    // journée entière et ne doit donc PAS rouvrir la pose (même règle que le
    // décompte et que l'occupation des créneaux).
    const seulJour = a.date_debut === a.date_fin;
    const entier =
      (a.debut_demi === 'journee' && a.fin_demi === 'journee') ||
      (a.debut_demi === 'matin' && a.fin_demi === 'apres_midi');
    if (seulJour && !entier) return null;
    if (cle === a.date_debut && a.debut_demi === 'apres_midi') return null;
    if (cle === a.date_fin && a.fin_demi === 'matin') return null;
    return a;
  };

  const cliquer = (cle: string) => {
    if (!posable(cle)) return;

    // Un jour entièrement pris se CONSULTE : proposer d'y poser une absence
    // reviendrait à proposer un geste que le serveur refusera pour cause de
    // chevauchement.
    const prise = occupeEntierement(cle);
    if (prise && !selection) {
      setConsultee(prise);
      return;
    }

    setConsultee(null);
    // Premier tap : on ouvre une sélection d'un jour. Second tap plus loin :
    // on étend jusque-là. Un tap sur le même jour la referme.
    if (!selection) return setSelection({ debut: cle, fin: cle });
    if (cle === selection.debut && cle === selection.fin) {
      setSelection(null);
      return;
    }
    if (cle < selection.debut) return setSelection({ debut: cle, fin: selection.fin });
    setSelection({ debut: selection.debut, fin: cle });
  };

  const changerMois = (n: number) => setMoisIso(moisDecale(moisIso, n));

  const unSeulJour = selection && selection.debut === selection.fin;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-titre text-xl font-bold text-nuit">Mon calendrier</h1>
        <NavigationMois valeur={moisIso} onChange={setMoisIso} />
      </header>

      {/* Tous les types se ressemblaient : sans le libellé et sans filtre, un
          calendrier chargé ne se lit plus. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ label="Filtrer par type">
          <Selection value={filtreType} onChange={(e) => setFiltreType(e.target.value)}>
            <option value="">Tous les types</option>
            {typesPresents.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Filtrer par statut">
          <Selection value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {Object.entries(LIBELLE_STATUT).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </Selection>
        </Champ>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-2">
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-nuit/50">
          {JOURS.map((j, i) => (
            <div key={i} className="py-1">
              {j}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: decalage }, (_, i) => (
            <div key={`vide-${i}`} />
          ))}
          {Array.from({ length: nbJours }, (_, i) => {
            const cle = iso(annee, mois, i + 1);
            const absence = parJour.get(cle);
            const libre = posable(cle);
            const dansSelection = selection && cle >= selection.debut && cle <= selection.fin;
            const teinte = absence ? TEINTE_STATUT[absence.statut] ?? '' : '';
            return (
              <button
                key={cle}
                type="button"
                disabled={!libre}
                onClick={() => cliquer(cle)}
                title={absence ? `${absence.type_libelle} : ${LIBELLE_STATUT[absence.statut]}` : undefined}
                // Cible tactile d'au moins 44 px : ce calendrier se pilote au
                // pouce, dans une gare ou sur un parking.
                className={`flex min-h-14 flex-col items-center justify-start rounded-md px-0.5 py-1 text-sm transition-colors ${
                  !libre
                    ? 'cursor-default bg-slate-50 text-slate-300'
                    : dansSelection
                      ? 'bg-nuit text-white'
                      : teinte || 'text-nuit hover:bg-ciel/30'
                }`}
              >
                <span className="leading-none">{i + 1}</span>
                {/* Le type, en toutes lettres autant que la case le permet :
                    la couleur seule dit le statut, jamais de quoi il s'agit. */}
                {absence && (
                  <span className="mt-0.5 w-full truncate text-[10px] leading-tight opacity-90">
                    {absence.type_libelle ?? 'Absence'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-nuit/60">
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-vert-fond/60" /> approuvée
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-jaune-fond/50" /> en attente
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-slate-50 ring-1 ring-slate-200" /> week-end
          ou férié
        </span>
      </div>

      {/* Cliquer sur un jour déjà pris ouvre l'absence, pas un formulaire. */}
      {consultee && (
        <Section titre={consultee.type_libelle ?? 'Absence'} teinte="ciel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-nuit">{periodeFr(consultee)}</p>
              <p className="text-sm text-nuit/60">{consultee.nb_jours} jour(s)</p>
              {consultee.description && (
                <p className="mt-1 text-sm text-nuit/70">{consultee.description}</p>
              )}
            </div>
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                COULEUR_STATUT[consultee.statut] ?? 'bg-slate-100 text-slate-700'
              }`}
            >
              {LIBELLE_STATUT[consultee.statut] ?? consultee.statut}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to={`/conges/demandes/${consultee.id}?depuis=/conges/calendrier`}>
              <BoutonSecondaire type="button">Ouvrir la demande</BoutonSecondaire>
            </Link>
            <BoutonSecondaire type="button" onClick={() => setConsultee(null)}>
              Fermer
            </BoutonSecondaire>
          </div>
        </Section>
      )}

      {/* La pose se fait SOUS le calendrier : quitter l'écran pour un
          formulaire séparé faisait perdre de vue ce qu'on venait d'y désigner. */}
      {selection && !consultee && (
        <Section
          titre={
            unSeulJour
              ? `Poser une absence le ${selection.debut.split('-').reverse().join('/')}`
              : `Poser une absence du ${selection.debut.split('-').reverse().join('/')} au ${selection.fin
                  .split('-')
                  .reverse()
                  .join('/')}`
          }
          teinte="vert"
        >
          {/* Le formulaire est LÀ, tout de suite : pas d'écran intermédiaire.
              Le raccourci « Journée / Matin / Après-midi » qui se trouvait ici
              a été retiré le 30/09/2026 — le formulaire porte désormais une
              case « c'est une demi-journée », et deux commandes pour la même
              information finissent par se contredire : le raccourci s'allumait
              sur « Journée entière » pendant que le formulaire envoyait encore
              « matin ». Une seule commande, dans le formulaire. */}
          <FormulaireDemande
            compact
            initial={{ debut: selection.debut, fin: selection.fin }}
            onTermine={(id) => {
              setSelection(null);
              if (id) navigate(`/conges/demandes/${id}`);
            }}
            onAnnuler={() => setSelection(null)}
          />
        </Section>
      )}
    </div>
  );
}
